import { ingestEvents } from '@/atoms/stats'
import { SubCloser } from '@nostr/tools/abstract-pool'
import { parse } from '@nostr/tools/nip27'
import { compareEvents } from '@/lib/event'
import { usePrimaryPage, useSecondaryPage } from '@/PageManager'
import { notificationListStyleAtom } from '@/atoms/preferences'
import client from '@/services/client.service'
import { NostrEvent } from '@nostr/tools/wasm'
import { matchFilter } from '@nostr/tools/filter'
import { loadRelayList } from '@nostr/gadgets/lists'
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react'
import { useTranslation } from 'react-i18next'
import PullToRefresh from 'react-simple-pull-to-refresh'
import { ShortNoteView, ShortNoteViewSkeleton } from './ShortNoteView'
import { isTouchDevice, now } from '@/lib/utils'
import { RefreshButton } from './RefreshButton'
import { Input } from '@/components/ui/input'
import { TFeedSubRequest } from '@/types'
import { replyKinds } from '@/lib/notification'
import { useCurrentPubkey } from '@/atoms/session'
import { useAtom, useAtomValue } from 'jotai'
import { lastReadNotificationTimeAtom, readNotificationIdSetAtom } from '@/atoms/notifications'
import { mutePubkeySetAtom } from '@/atoms/lists'
import { toNote } from '@/lib/link'

const LIMIT = 100
const SHOW_COUNT = 30

const ConversationList = forwardRef((_, ref) => {
  const { t } = useTranslation()
  const { current, display } = usePrimaryPage()
  const active = useMemo(() => current === 'conversations' && display, [current, display])
  const pubkey = useCurrentPubkey()
  const notificationListStyle = useAtomValue(notificationListStyleAtom)
  const mutePubkeySet = useAtomValue(mutePubkeySetAtom)
  const [refreshCount, setRefreshCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [conversations, setConversations] = useState<NostrEvent[]>([])
  const [subRequests, setSubRequests] = useState<null | TFeedSubRequest[]>(null)
  const [showCount, setShowCount] = useState(SHOW_COUNT)
  const [until, setUntil] = useState<number | undefined>(now())
  const [userFilter, setUserFilter] = useState('')
  const [matchingPubkeys, setMatchingPubkeys] = useState<Set<string> | null>(null)
  const lastReadTime = useAtomValue(lastReadNotificationTimeAtom)
  const { push } = useSecondaryPage()
  const [notificationIdSet, setNotificationIdSet] = useAtom(readNotificationIdSetAtom)
  const supportTouch = useMemo(() => isTouchDevice(), [])
  const topRef = useRef<HTMLDivElement | null>(null)
  const bottomRef = useRef<HTMLDivElement | null>(null)

  // Search for matching pubkeys when user filter changes
  useEffect(() => {
    if (!userFilter.trim()) {
      setMatchingPubkeys(null)
      return
    }

    const searchProfiles = async () => {
      try {
        const pubkeys = client.searchPubKeysFromLocal(userFilter, 1000)
        setMatchingPubkeys(new Set(pubkeys))
      } catch (e) {
        console.error('Error searching profiles:', e)
        setMatchingPubkeys(new Set())
      }
    }

    searchProfiles()
  }, [userFilter])

  // Apply user filter (by author name or content)
  const filteredConversations = useMemo(() => {
    if (!userFilter.trim()) {
      return conversations
    }

    const filterLower = userFilter.toLowerCase()
    return conversations.filter((event) => {
      // Check if author matches
      if (matchingPubkeys && matchingPubkeys.has(event.pubkey)) {
        return true
      }

      // Check if content matches
      if (event.content && event.content.toLowerCase().includes(filterLower)) {
        return true
      }

      return false
    })
  }, [conversations, userFilter, matchingPubkeys])

  useImperativeHandle(
    ref,
    () => ({
      refresh: () => {
        if (loading) return
        setRefreshCount((count) => count + 1)
      }
    }),
    [loading]
  )

  useEffect(() => {
    if (current !== 'conversations') return
    if (!pubkey) {
      setUntil(undefined)
      return
    }

    setLoading(true)
    setConversations([])
    setShowCount(SHOW_COUNT)

    let subc: SubCloser | undefined

    // Start with local DB immediately, no waiting for relay list fetch
    const localSubRequests: TFeedSubRequest[] = [
      {
        source: 'local',
        filter: {
          '#p': [pubkey],
          kinds: replyKinds
        }
      },
      {
        source: 'local',
        filter: {
          authors: [pubkey],
          kinds: replyKinds
        }
      }
    ]

    subc = client.subscribeTimeline(
      localSubRequests,
      { limit: LIMIT },
      {
        onEvents: (events, isFinal) => {
          if (events.length > 0) {
            const conversations = events.filter(
              (evt) => evt.pubkey === pubkey || !eventMentionsPubkey(evt, pubkey)
            )
            setConversations(conversations)
          }

          if (isFinal) {
            setUntil(events.length > 0 ? events[events.length - 1].created_at - 1 : undefined)
            ingestEvents(events)
          }
        },
        onNew: (event) => {
          if (event.pubkey === pubkey || !eventMentionsPubkey(event, pubkey)) handleNewEvent(event)
        }
      }
    )

    // Then add relay sources when relay list is available
    ;(async () => {
      const { items } = await loadRelayList(pubkey)

      subc?.close()

      const fullSubRequests: TFeedSubRequest[] = [
        {
          source: 'relays',
          urls: items.filter((r) => r.read).map((r) => r.url),
          filter: {
            '#p': [pubkey],
            kinds: replyKinds
          }
        },
        {
          source: 'local',
          filter: {
            '#p': [pubkey],
            kinds: replyKinds
          }
        },
        {
          source: 'local',
          filter: {
            authors: [pubkey],
            kinds: replyKinds
          }
        }
      ]

      setSubRequests(fullSubRequests)

      subc = client.subscribeTimeline(
        fullSubRequests,
        { limit: LIMIT },
        {
          onEvents: (events, isFinal) => {
            if (events.length > 0) {
              const conversations = events.filter(
                (evt) => evt.pubkey === pubkey || !eventMentionsPubkey(evt, pubkey)
              )
              setConversations(conversations)
            }

            if (isFinal) {
              setUntil(events.length > 0 ? events[events.length - 1].created_at - 1 : undefined)
              ingestEvents(events)
            }
          },
          onNew: (event) => {
            if (event.pubkey === pubkey || !eventMentionsPubkey(event, pubkey))
              handleNewEvent(event)
          }
        }
      )
    })()

    return () => subc?.close?.()
  }, [pubkey, refreshCount, current])

  useEffect(() => {
    if (!active || !pubkey) return

    const handler = (data: Event) => {
      const customEvent = data as CustomEvent<NostrEvent>
      const evt = customEvent.detail
      if (
        matchFilter(
          {
            kinds: replyKinds,
            '#p': [pubkey]
          },
          evt
        ) ||
        matchFilter(
          {
            kinds: replyKinds,
            authors: [pubkey]
          },
          evt
        )
      ) {
        handleNewEvent(evt)
      }
    }

    client.addEventListener('newEvent', handler)
    return () => {
      client.removeEventListener('newEvent', handler)
    }
  }, [pubkey, active])

  const loadingRef = useRef(false)
  const showCountRef = useRef(SHOW_COUNT)
  const filteredConversationsRef = useRef<NostrEvent[]>([])
  const subRequestsRef = useRef<null | TFeedSubRequest[]>([])
  const pubkeyRef = useRef<string | null>(pubkey)
  const untilRef = useRef<number | undefined>(until)
  const loadMoreRef = useRef<() => Promise<void>>(async () => {})

  useEffect(() => {
    showCountRef.current = showCount
  }, [showCount])

  useEffect(() => {
    filteredConversationsRef.current = filteredConversations
  }, [filteredConversations])

  useEffect(() => {
    subRequestsRef.current = subRequests
  }, [subRequests])

  useEffect(() => {
    pubkeyRef.current = pubkey
  }, [pubkey])

  useEffect(() => {
    untilRef.current = until
  }, [until])

  const loadMore = useCallback(async () => {
    if (loadingRef.current) return

    const currentPubkey = pubkeyRef.current
    const currentSubRequests = subRequestsRef.current
    const currentShowCount = showCountRef.current
    const currentFiltered = filteredConversationsRef.current
    const currentUntil = untilRef.current

    if (!currentPubkey || !currentSubRequests || currentSubRequests.length === 0) return

    setShowCount((count) => count + SHOW_COUNT)

    if (currentFiltered.length - (currentShowCount + SHOW_COUNT) > LIMIT / 2) {
      return
    }

    if (!currentUntil) return

    loadingRef.current = true
    setLoading(true)
    try {
      const newConversations = (
        await client.loadMoreTimeline(currentSubRequests, {
          until: currentUntil,
          limit: LIMIT
        })
      ).filter((evt) => evt.pubkey === currentPubkey || !eventMentionsPubkey(evt, currentPubkey))

      if (newConversations.length === 0) {
        setUntil(undefined)
        return
      }

      setConversations((old) => [...old, ...newConversations])
      setUntil(newConversations[newConversations.length - 1].created_at - 1)
    } finally {
      loadingRef.current = false
      setLoading(false)
    }
  }, [])

  loadMoreRef.current = loadMore

  useEffect(() => {
    if (!until || loading) return

    const observerInstance = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMoreRef.current()
        }
      },
      { root: null, rootMargin: '10px', threshold: 1 }
    )

    const currentBottomRef = bottomRef.current

    if (currentBottomRef) {
      observerInstance.observe(currentBottomRef)
    }

    return () => {
      if (observerInstance && currentBottomRef) {
        observerInstance.unobserve(currentBottomRef)
      }
    }
  }, [until, loading])

  const list = (
    <div className={notificationListStyle === 'compact' ? 'pt-2' : ''}>
      {filteredConversations
        .filter((conversation) => !mutePubkeySet.has(conversation.pubkey))
        .slice(0, showCount)
        .map((conversation) => {
          const unread =
            conversation.created_at > lastReadTime && !notificationIdSet.has(conversation.id)
          return (
            <ShortNoteView
              key={conversation.id}
              event={conversation}
              style={notificationListStyle}
              bold={unread}
              onClick={() => {
                setNotificationIdSet((prev) => {
                  const next = new Set(prev)
                  next.add(conversation.id)
                  return next
                })
                push(toNote(conversation))
              }}
            />
          )
        })}
      <div className="text-center text-sm text-muted-foreground">
        {until || loading ? (
          <div ref={bottomRef}>
            <ShortNoteViewSkeleton style={notificationListStyle} />
          </div>
        ) : (
          t('no more conversations')
        )}
      </div>
    </div>
  )

  return (
    <div>
      <div className="sticky flex items-center justify-between top-12 bg-background z-30 px-4 py-2 w-full border-b gap-3">
        <div
          tabIndex={0}
          className="relative flex w-full items-center rounded-md border border-input px-3 py-1 text-base transition-colors md:text-sm [&:has(:focus-visible)]:ring-ring [&:has(:focus-visible)]:ring-1 [&:has(:focus-visible)]:outline-none bg-surface-background shadow-inner h-full border-none"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="lucide lucide-search size-4 shrink-0 opacity-50"
          >
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.3-4.3"></path>
          </svg>

          <Input
            type="text"
            placeholder={t('Filter by author or content...')}
            value={userFilter}
            onChange={(e) => setUserFilter(e.target.value)}
            showClearButton={true}
            onClear={() => setUserFilter('')}
            className="flex-1 h-9 size-full shadow-none border-none bg-transparent focus:outline-none focus-visible:outline-none focus-visible:ring-0 placeholder:text-muted-foreground"
          />
        </div>
        {!supportTouch && <RefreshButton onClick={() => refresh()} />}
      </div>
      <div ref={topRef} className="scroll-mt-[calc(6rem+1px)]" />
      {supportTouch ? (
        <PullToRefresh
          onRefresh={async () => {
            refresh()
            await new Promise((resolve) => setTimeout(resolve, 1000))
          }}
          pullingContent=""
        >
          {list}
        </PullToRefresh>
      ) : (
        list
      )}
    </div>
  )

  function refresh() {
    topRef.current?.scrollIntoView({ behavior: 'instant', block: 'start' })
    setTimeout(() => {
      setRefreshCount((count) => count + 1)
    }, 500)
  }

  function eventMentionsPubkey(event: NostrEvent, pubkey: string): boolean {
    for (const block of parse(event.content)) {
      // only check for explicit mentions in content (e.g., nostr:npub... references)
      if (block.type === 'reference' && 'pubkey' in block && block.pubkey === pubkey) {
        // this is a mention
        return true
      }
    }

    return false
  }

  function handleNewEvent(event: NostrEvent) {
    setConversations((oldEvents) => {
      const index = oldEvents.findIndex((oldEvent) => compareEvents(oldEvent, event) <= 0)
      if (index !== -1 && oldEvents[index].id === event.id) {
        return oldEvents
      }

      ingestEvents([event])
      if (index === -1) {
        return [...oldEvents, event]
      }
      return [...oldEvents.slice(0, index), event, ...oldEvents.slice(index)]
    })
  }
})

ConversationList.displayName = 'ConversationList'
export default ConversationList
